'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { ColumnSelector, MultiColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { Plus, Trash, Info, CaretDown } from '@phosphor-icons/react';

interface AggregateField {
  column: string;
  function: 'sum' | 'count' | 'avg' | 'max' | 'min' | 'distinct_count';
  alias?: string;
  decimals?: number;
  rounding?: 'round' | 'rounddown' | 'roundup' | 'truncate' | 'banker';
}

interface AggregateConfigProps {
  nodeId: string;
  config: {
    groupBy?: string[];
    aggregations?: AggregateField[];
  };
}

export function AggregateConfig({ nodeId, config }: AggregateConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const [groupBy, setGroupBy] = useState<string[]>(config.groupBy || []);
  const [aggregations, setAggregations] = useState<AggregateField[]>(
    config.aggregations || [{ column: '', function: 'sum' }]
  );
  const inputColumns = useNodeInputColumns(nodeId);

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.groupBy) {
      setGroupBy(config.groupBy);
    }
    if (config.aggregations && config.aggregations.length > 0) {
      setAggregations(config.aggregations);
    }
  }, [config.groupBy, config.aggregations]);

  const updateGroupBy = (columns: string[]) => {
    setGroupBy(columns);
    updateNodeConfig(nodeId, {
      type: 'aggregate',
      params: { groupBy: columns, aggregations },
    });
  };

  const updateAggregation = (index: number, updates: Partial<AggregateField>) => {
    const newAggregations = aggregations.map((a, i) =>
      i === index ? { ...a, ...updates } : a
    );
    setAggregations(newAggregations);
    updateNodeConfig(nodeId, {
      type: 'aggregate',
      params: { groupBy, aggregations: newAggregations },
    });
  };

  const addAggregation = () => {
    const newAggregations = [
      ...aggregations,
      { column: '', function: 'sum' as const, decimals: 2, rounding: 'round' as const },
    ];
    setAggregations(newAggregations);
    updateNodeConfig(nodeId, {
      type: 'aggregate',
      params: { groupBy, aggregations: newAggregations },
    });
  };

  const removeAggregation = (index: number) => {
    const newAggregations = aggregations.filter((_, i) => i !== index);
    setAggregations(newAggregations);
    updateNodeConfig(nodeId, {
      type: 'aggregate',
      params: { groupBy, aggregations: newAggregations },
    });
  };

  const functions = [
    { value: 'sum', label: t('nodes.config.aggregate.sum'), icon: 'Σ' },
    { value: 'count', label: t('nodes.config.aggregate.count'), icon: '#️⃣' },
    { value: 'avg', label: t('nodes.config.aggregate.avg'), icon: 'x̄' },
    { value: 'max', label: t('nodes.config.aggregate.max'), icon: '↑' },
    { value: 'min', label: t('nodes.config.aggregate.min'), icon: '↓' },
    { value: 'distinct_count', label: t('nodes.config.aggregate.distinctCount'), icon: '◊' },
  ];

  const roundingMethods = [
    { value: 'round', label: t('nodes.config.aggregate.round'), description: t('nodes.config.aggregate.roundDesc') },
    { value: 'rounddown', label: t('nodes.config.aggregate.roundDown'), description: t('nodes.config.aggregate.roundDownDesc') },
    { value: 'roundup', label: t('nodes.config.aggregate.roundUp'), description: t('nodes.config.aggregate.roundUpDesc') },
    { value: 'truncate', label: t('nodes.config.aggregate.truncate'), description: t('nodes.config.aggregate.truncateDesc') },
    { value: 'banker', label: t('nodes.config.aggregate.banker'), description: t('nodes.config.aggregate.bankerDesc') },
  ];

  return (
    <div className="space-y-5">
      <div className="text-sm font-medium text-zinc-700">{t('nodes.config.aggregate.title')}</div>

      {/* Group By */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.aggregate.groupBy')}
        </label>
        <MultiColumnSelector
          values={groupBy}
          onChange={updateGroupBy}
          columns={inputColumns ?? []}
        />
        <p className="text-xs text-zinc-400">
          {t('nodes.config.aggregate.groupByHint')}
        </p>
      </div>

      {/* Aggregations */}
      <div className="space-y-3">
        <div className="text-xs font-medium text-zinc-500">{t('nodes.config.aggregate.aggregations')}</div>
        
        <AnimatePresence>
          {aggregations.map((agg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-3"
            >
              {/* Column & Remove */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <ColumnSelector
                    value={agg.column}
                    onChange={(columnName) =>
                      updateAggregation(index, { column: columnName })
                    }
                    placeholder={inputColumns ? t('nodes.config.aggregate.selectColumn') : t('nodes.config.aggregate.connectInputFirst')}
                    filterType="number"
                    columns={inputColumns ?? []}
                  />
                </div>
                {aggregations.length > 1 && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => removeAggregation(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash className="w-4 h-4" />
                  </motion.button>
                )}
              </div>
              
              {/* Function */}
              <div className="grid grid-cols-3 gap-2">
                {functions.map((fn) => (
                  <motion.button
                    key={fn.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      updateAggregation(index, {
                        function: fn.value as AggregateField['function'],
                      })
                    }
                    className={`
                      px-3 py-2 text-xs font-medium rounded-lg border transition-all
                      ${agg.function === fn.value
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                      }
                    `}
                  >
                    {fn.label}
                  </motion.button>
                ))}
              </div>
              
              {/* Decimals & Rounding Method */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500 whitespace-nowrap">{t('nodes.config.aggregate.decimals')}</label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={agg.decimals ?? 2}
                    onChange={(e) =>
                      updateAggregation(index, { decimals: parseInt(e.target.value) || 0 })
                    }
                    className="w-16 text-sm bg-white border border-zinc-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
                <div className="flex-1 relative">
                  <select
                    value={agg.rounding ?? 'round'}
                    onChange={(e) =>
                      updateAggregation(index, { rounding: e.target.value as AggregateField['rounding'] })
                    }
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 appearance-none cursor-pointer"
                  >
                    {roundingMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                  <CaretDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                </div>
              </div>
              
              {/* Alias */}
              <input
                type="text"
                placeholder={t('nodes.config.aggregate.aliasPlaceholder')}
                value={agg.alias || ''}
                onChange={(e) =>
                  updateAggregation(index, { alias: e.target.value })
                }
                className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
              />
            </motion.div>
          ))}
        </AnimatePresence>
        
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={addAggregation}
          className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('nodes.config.aggregate.addAggregation')}
        </motion.button>
      </div>

      {/* Info Tip */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700 leading-relaxed space-y-1">
          <p>{t('nodes.config.aggregate.aggregateTip')}</p>
          <p className="text-blue-600">
            {t('nodes.config.aggregate.roundingTip')}
          </p>
        </div>
      </div>
    </div>
  );
}
